import { s3ProgressConfig } from "../utils/uploadProgress";
import { api } from "./axiosClient";
import type { BusinessUnit } from "../types/businessUnit.types";
import axios from "axios";

/**
 * The organisation's business-unit ids, which is how departments are scoped
 * (a department's search document carries its business unit, not its
 * organisation). Every departments load used to look them up again, beside
 * the page's own business-unit list — so kept for a minute per organisation,
 * and dropped whenever a unit is created or removed, the only changes that
 * alter the set.
 */
const ORG_UNIT_IDS_TTL_MS = 60_000;
const orgUnitIds = new Map<string, { at: number; ids: Promise<string[]> }>();

const forgetOrgUnitIds = () => orgUnitIds.clear();

export const businessUnitsApi = {
  /** The ids of every business unit in the organisation (see orgUnitIds). */
  getOrganisationUnitIds: (companyId: string): Promise<string[]> => {
    const cached = orgUnitIds.get(companyId);
    if (cached && Date.now() - cached.at < ORG_UNIT_IDS_TTL_MS) {
      return cached.ids;
    }
    const ids = api
      .post("/businessunit/Search", {
        pageSize: 1000,
        pageIndex: 0,
        groupIds: [companyId],
      })
      .then((response) =>
        ((response.data?.data as { id: string }[]) || []).map((bu) => bu.id),
      );
    orgUnitIds.set(companyId, { at: Date.now(), ids });
    // A failed lookup is not remembered.
    ids.catch(() => orgUnitIds.delete(companyId));
    return ids;
  },

  searchBusinessUnits: async (payload: any) => {
    const response = await api.post("/businessunit/Search", payload);
    return response.data;
  },

  /** Upload links for a business unit's photos and documents (its own store). */
  initUpload: async (ids: string[], groupId?: string) => {
    const response = await api.post("/businessunit/initupload", {
      ids,
      ...(groupId ? { groupId } : {}),
    });
    return response.data;
  },

  uploadToS3: async (s3Url: string, file: File) => {
    return await axios.put(s3Url, file, {
      ...s3ProgressConfig(file),
      headers: { "Content-Type": file.type },
    });
  },

  getMediaLink: async (ids: string[], size: number = 1, groupId?: string) => {
    const response = await api.post("/businessunit/getmedialink", {
      ids,
      size,
      ...(groupId ? { groupId } : {}),
    });
    return response.data;
  },

  createBusinessUnit: async (data: Partial<BusinessUnit>) => {
    const response = await api.post("/businessunit/create", data);
    forgetOrgUnitIds();
    return response.data;
  },

  updateBusinessUnit: async (data: Partial<BusinessUnit>) => {
    const response = await api.post("/businessunit/edit", data);
    return response.data;
  },

  activateBusinessUnit: async (id: string) => {
    return await api.post("/businessunit/activate", { id });
  },

  deActivateBusinessUnit: async (id: string) => {
    return await api.post("/businessunit/deactivate", { id });
  },

  removeBusinessUnit: async (id: string) => {
    const response = await api.post("/businessunit/remove", { id });
    forgetOrgUnitIds();
    return response;
  },
};
